import type { BrandAndProductProps } from '../../components/ExternalBrandLogo';
import safeParseUrl from './safeParseUrl';
import UrlRule, { firstResult } from './UrlRule';

const isVideoUrl = (url: URL) =>
  url.protocol === 'https:' &&
  (url.pathname.endsWith('.mp4') || url.pathname.endsWith('.mov'));

export const VIDEO_SOURCES: {
  [name: string]: {
    rules: UrlRule[];
    formatters: {
      embedUrl: UrlRule;
      fullUrl?: UrlRule;
    };
    iconProps: BrandAndProductProps;
    renderAsIFrame?: boolean;
  };
} = {
  youtube: {
    rules: [
      new UrlRule({
        host: ':_?.youtube.com',
        path: '/watch',
        queries: { v: ':id([\\w-]+)', list: ':list([\\w-]+)?' },
      }),
      new UrlRule({
        host: ':_?.youtube.com',
        path: '/playlist',
        queries: { list: ':list([\\w-]+)' },
      }),
      new UrlRule({
        host: ':_?.youtube.com',
        path: '/embed/videoseries',
        queries: { list: ':list([\\w-]+)?' },
      }),
      new UrlRule({
        host: ':_?.youtube.com',
        path: '/embed/:id([\\w-]+)',
        queries: { list: ':list([\\w-]+)?' },
      }),
      new UrlRule({
        host: ':_?.youtube.com',
        path: '/shorts/:id([\\w-]+)',
      }),
      new UrlRule({ host: ':_?.youtu.be', path: '/:id([\\w-]+)' }),
    ],
    formatters: {
      embedUrl: new UrlRule({
        host: 'www.youtube.com',
        path: '/embed/:id?',
        queries: { showinfo: '0', list: ':list?' },
      }),
      fullUrl: new UrlRule({
        host: 'youtu.be',
        path: '/:id',
        queries: { list: ':list?' },
      }),
    },
    iconProps: {
      brand: 'google',
      product: 'youtube',
    },
    renderAsIFrame: true,
  },
  vimeo: {
    rules: [
      new UrlRule({
        host: ':_(www)?.vimeo.com',
        path: '/:id(\\d+)/:hash?',
      }),
      new UrlRule({
        host: 'player.vimeo.com',
        path: '/video/:id(\\d+)',
        queries: { h: ':hash?' },
      }),
      new UrlRule({ host: 'vimeo.com', path: '/channels/:_channel/:id(\\d+)' }),
    ],
    formatters: {
      embedUrl: new UrlRule({
        host: 'player.vimeo.com',
        path: '/video/:id',
        queries: { h: ':hash?' },
      }),
      fullUrl: new UrlRule({ host: 'vimeo.com', path: '/:id/:hash?' }),
    },
    iconProps: {
      brand: 'vimeo',
    },
    renderAsIFrame: true,
  },
  'wistia-playlist': {
    rules: [
      new UrlRule({
        host: ':_.wistia.com',
        path: '/projects/:id([a-z0-9]+)/build_playlist',
      }),
    ],
    formatters: {
      embedUrl: new UrlRule({
        host: 'fast.wistia.net',
        path: '/embed/playlists/:id',
        queries: {
          theme: 'tab',
          'videoOptions[version]': 'v1.1',
          'media_0_0[autoPlay]': 'false',
        },
      }),
    },
    iconProps: {
      brand: 'wistia',
    },
    renderAsIFrame: true,
  },
  'wistia-video': {
    rules: [
      new UrlRule({ host: ':_.wistia.com', path: '/medias/:id([a-z0-9]+)' }),
    ],
    formatters: {
      embedUrl: new UrlRule({
        host: 'fast.wistia.net',
        path: '/embed/iframe/:id',
        queries: { autoPlay: 'false' },
      }),
    },
    iconProps: {
      brand: 'wistia',
    },
  },
  loom: {
    rules: [
      new UrlRule({
        host: ':_(www)?.loom.com',
        path: '/:type(share|embed)/:id([a-z0-9]+)',
      }),
    ],
    formatters: {
      embedUrl: new UrlRule({ host: 'www.loom.com', path: '/embed/:id' }),
      fullUrl: new UrlRule({ host: 'www.loom.com', path: '/share/:id' }),
    },
    iconProps: {
      brand: 'loom',
    },
    renderAsIFrame: true,
  },
  dropbox: {
    rules: [
      new UrlRule({
        host: ':_.dropbox.com',
        path: '/scl/fi/:id([a-zA-Z0-9]+)/:name.:ext(mp4|mov)',
        queries: { rlkey: ':rlkey' },
      }),
    ],
    formatters: {
      embedUrl: new UrlRule({
        host: 'dropbox.com',
        path: '/scl/fi/:id/:name.:ext',
        queries: { raw: '1', rlkey: ':rlkey' },
      }),
      fullUrl: new UrlRule({
        host: 'www.dropbox.com',
        path: '/scl/fi/:id/:name.:ext',
        queries: { rlkey: ':rlkey' },
      }),
    },
    iconProps: {
      brand: 'dropbox',
    },
  },
};

const sources = Object.values(VIDEO_SOURCES);

const matchVideo = (text: string) => {
  const url = safeParseUrl(text);
  if (!url) return null;
  const embededUrl = firstResult(sources, (source) => {
    const matched = firstResult(source.rules, (rule) => rule.match(url));
    return matched ? source.formatters.embedUrl.format(matched) : null;
  });
  if (embededUrl) {
    return embededUrl;
  }
  return isVideoUrl(url) ? url.toString() : null;
};

export const getFullUrl = (embedUrl: string) => {
  const url = safeParseUrl(embedUrl);
  if (!url) return embedUrl;
  return (
    firstResult(sources, (source) => {
      if (!source.formatters.fullUrl) return null;
      const matched = source.formatters.embedUrl.match(url);
      return matched ? source.formatters.fullUrl.format(matched) : null;
    }) || embedUrl
  );
};

export const needRenderAsIFrame = (rawUrl: string) => {
  const url = safeParseUrl(rawUrl);
  if (!url) return false;
  return sources.some(
    ({ formatters: { embedUrl }, renderAsIFrame }) =>
      renderAsIFrame && embedUrl.testHost(url.host),
  );
};

export default matchVideo;
